import { useMutation, useQueryClient } from '@tanstack/react-query';

import { deleteChatApi } from '../../api/chats';

import { CHAT_QUERY_KEY } from './use-chat';
import { CHATS_QUERY_KEY } from './use-chats';
import { removeChatFromCache, type ChatsCacheData } from './chats-cache';

/**
 * Hook to delete a chat.
 *
 * Optimistically removes the row from every cached chats list
 * (dashboard, sidebar, chats page each use a different limit/offset,
 * so they're stored under separate sub-keys). Also drops the per-id detail
 * cache so a navigation to /chat/$id right after delete doesn't
 * render a stale row. Rolls back the list snapshot on error.
 */
export function useDeleteChat() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deleteChatApi,
    onMutate: async (chatId: string) => {
      await queryClient.cancelQueries({ queryKey: [CHATS_QUERY_KEY] });

      const previousLists = queryClient.getQueriesData<ChatsCacheData>({
        queryKey: [CHATS_QUERY_KEY],
      });

      queryClient.setQueriesData<ChatsCacheData>({ queryKey: [CHATS_QUERY_KEY] }, (old) =>
        removeChatFromCache(old, chatId)
      );

      // Drop the detail cache so a follow-up navigation cannot render the
      // deleted row from a stale `useChat` cache.
      queryClient.removeQueries({ queryKey: [CHAT_QUERY_KEY, chatId] });

      return { previousLists };
    },
    onError: (_err, _id, context) => {
      if (context?.previousLists) {
        for (const [key, value] of context.previousLists) {
          queryClient.setQueryData(key, value);
        }
      }
    },
    onSettled: () => {
      // Reconcile with server (covers concurrent edits the optimistic path
      // couldn't predict — title/summary changes on other rows, etc.).
      void queryClient.invalidateQueries({ queryKey: [CHATS_QUERY_KEY] });
    },
  });
}
